<template>
  <v-container class="pa-0">
    <template v-if="isLoaded && (mapPanelDataFetched || admin)">
      <v-row no-gutters>
        <v-col cols="9">
          <v-row no-gutters align="baseline" v-if="isDataSourceSelectable">
            <v-col cols="6" class="text-left"
              >{{
                dataSourceInputMetadata.label ? dataSourceInputMetadata.label : $i18n.t('dialog.dataSource')
              }}:</v-col
            >
            <v-col cols="6">
              <data-input
                :rules="[v => !!v || $i18n.t('rules.required')]"
                :items="computedDataSources"
                @change="onDataSourceChange"
                :menu-props="{ maxWidth: '350px' }"
                v-model="computedMainDataSource"
                item-text="verbose_name"
                item-value="name"
                :data-type="dataType"
                :disabled="!isDataSourceChangable"
              />
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <slot name="inputs" />
      <v-row class="mt-5" :class="{ 'border-top': isDataSourceSelectable }" no-gutters>
        <v-col cols="12">
          <v-form ref="form" v-model="isValid">
            <expression-group
              :data-source-name="computedMainDataSource"
              :data-source-attributes="dataSourceAttributes"
              :data-source-attributes-relational="dataSourceAttributesRelational"
              :is-main="true"
              v-model="computedValue"
              @clearAllFilteres="clearAllFilteres"
            />
          </v-form>
        </v-col>
      </v-row>
    </template>
    <template v-else>
      <main-loader />
    </template>
  </v-container>
</template>

<script>
import { get, call } from 'vuex-pathify';

import ExpressionGroup from '@/components/ExpressionGroup';
import MainLoader from '@/components/MainLoader';
import DataInput from '@/components/DataInput';

export default {
  name: 'ExpressionBuilder',
  components: {
    DataInput,
    ExpressionGroup,
    MainLoader,
  },
  props: {
    value: {
      type: Object,
      default: null,
    },
    mainDataSource: {
      type: String,
      default: '',
    },
    isDataSourceSelectable: {
      type: Boolean,
      default: true,
    },
    type: {
      type: String,
      default: '',
    },
    isDataSourceChangable: {
      type: Boolean,
      default: true,
    },
    dataSourceInputMetadata: {
      type: Object,
      default: () => ({}),
    },
    customDataSourceAttributes: {
      type: Array,
    },
    admin: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    isLoaded: false,
    dataType: {
      name: 'select',
    },
    isValid: false,
    dataSourceAttributesRelational: [],
  }),
  computed: {
    mapPanelDataFetched: get('map/mapPanelDataFetched'),
    catalogs: get('admin/modulesMapping@zdm_data.catalogs'),
    layersMetadata: get('layers/metadata'),
    additionalLayersMetadata: get('layers/additionalMetadata'),
    dataSources() {
      return { ...this.layersMetadata, ...this.additionalLayersMetadata };
    },
    expressionsMetadata: get('layers/expressions'),
    computedMainDataSource: {
      get() {
        return this.mainDataSource;
      },
      set(nV) {
        this.$emit('update:mainDataSource', nV);
      },
    },
    mainDataSourceUseQualifiedNames() {
      return this.layersMetadata[this.computedMainDataSource]?.attributes_use_datasource_qualified_names;
    },
    computedValue: {
      get() {
        return this.value;
      },
      set(nV) {
        this.$emit('input', nV || null);
      },
    },
    computedCatalogsDataSourcesNames() {
      return this.catalogs ? Object.values(this.catalogs).map(catalog => catalog.datasource) : [];
    },
    computedDataSources() {
      const dataSources = Object.values(this.layersMetadata).filter(value => value.scope === this.type);
      if (this.type === 'zdm_data') {
        return dataSources.filter(dataSource => !this.computedCatalogsDataSourcesNames.includes(dataSource.name));
      }
      return dataSources;
    },
    dataSourceAttributes() {
      if (this.customDataSourceAttributes) {
        return this.customDataSourceAttributes;
      }
      if (this.computedMainDataSource) {
        return this.layersMetadata[this.computedMainDataSource]?.attributes_schema.attributes;
      }
      return [];
    },
  },
  watch: {
    isValid(nV) {
      this.$emit('update:isValid', nV);
    },
    isLoaded: {
      handler(nV) {
        if (nV && this.dataSourceAttributes?.length > 0) {
          this.fetchRelationalMetadata([...this.dataSourceAttributes]);
        }
      },
    },
    dataSourceAttributes: {
      handler(nV) {
        if (this.isLoaded && nV?.length) {
          this.fetchRelationalMetadata([...nV]);
        }
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    getDataSources: call('layers/getLayersMetadata'),
    getDataSource: call('layers/getLayerMetadata'),
    getExpressions: call('layers/getExpressions'),
    reset() {
      this.computedMainDataSource = undefined;
      this.computedValue = undefined;
    },
    clearAllFilteres() {
      this.$refs.form?.reset();
      this.computedValue = undefined;
    },
    onDataSourceChange() {
      this.clearAllFilteres();
    },
    async init() {
      if (this.expressionsMetadata.length < 1) {
        await this.getExpressions();
      }
      if (this.admin) {
        await this.getDataSources({ with_attributes_schema: true, withAdditionalMetadata: true });
      }
      this.isLoaded = true;
      this.$emit('builderLoaded');
    },
    async fetchRelationalMetadata(attributes) {
      attributes = attributes.map(attribute => {
        return {
          ...attribute,
          value: this.mainDataSourceUseQualifiedNames
            ? attribute.name
            : `${attribute.datasource.name}.${attribute.name}`,
        };
      });
      const relationedAttributes = attributes.filter(
        attribute =>
          attribute.relation &&
          attributes.filter(att => att.relation?.data_source === attribute.relation.data_source).length === 1
      );
      const relationedDataSources = relationedAttributes
        .filter(
          (thing, index, self) => index === self.findIndex(t => t.relation.data_source === thing.relation.data_source)
        )
        .map(attribute => attribute.relation.data_source);
      const relationdDataSourcesMetadata = relationedDataSources.map(dataSource => {
        return {
          metadata: this.dataSources[dataSource],
          dataSource,
        };
      });
      const preparedResponses = relationdDataSourcesMetadata.reduce((total, current) => {
        const { attributes_use_datasource_qualified_names: useQualifiedNames } = current.metadata;
        return {
          ...total,
          [current.dataSource]: current.metadata.attributes_schema.attributes
            .filter(attribute => !attribute.relation)
            .map(attribute => {
              return {
                ...attribute,
                verbose_name: `${attribute.verbose_name} (${current.metadata.verbose_name})`,
                value: useQualifiedNames ? attribute.name : `${attribute.datasource.name}.${attribute.name}`,
              };
            }),
        };
      }, {});
      const mappedNames = attributes.map(attribute => attribute.name);
      this.dataSourceAttributesRelational = relationedAttributes.reduce((total, attribute) => {
        const index = mappedNames.indexOf(attribute.name);
        total.splice(index + 1, 0, ...preparedResponses[attribute.relation.data_source]);
        return total;
      }, attributes);
    },
  },
  created() {
    this.init();
  },
};
</script>

<style lang="scss" scoped>
.border-top {
  @include border(top, 1px);
}
.text-item {
  cursor: pointer;
}
.expression {
  background-color: rgba(234, 234, 244, 0.25);
  border: 0.6px solid rgba(0, 0, 0, 0.12);
  min-height: 60px;
}
::v-deep .v-list-item__title {
  font-family: Roboto;
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 16px;
  text-align: left;
}
::v-deep .v-input {
  font-size: 14px;
}
::v-deep .v-input__slot {
  margin-bottom: 0;
}
::v-deep .new-filter-button .v-icon {
  font-size: 18px;
}
</style>
